import { Pencil, X } from 'lucide-react';

import {
  defaultShouldIgnoreKeyboardEvent,
  useKeyboardShortcuts,
} from '@/hooks/use-keyboard-shortcuts';
import { cn } from '@/lib/utils';
import type { AgentChatDecision } from '@/services/agents/chat-stream';

interface DecisionPanelProps {
  decision: AgentChatDecision;
  onPick: (optionIndex: number) => void;
  /** "Something else" -- hand the composer back, keeping the question open. */
  onFreeText: () => void;
  onDismiss: () => void;
  disabled?: boolean;
}

/**
 * The composer, while the agent is waiting on an answer.
 *
 * It takes the prompt bar's place rather than sitting above it: a question
 * the user can also ignore by typing is a question they will ignore, and
 * the agent has already ended its turn on the assumption of an answer.
 * Both escapes stay one click away -- "Something else" to type instead,
 * dismiss to drop the question entirely.
 */
export function DecisionPanel({
  decision,
  onPick,
  onFreeText,
  onDismiss,
  disabled = false,
}: DecisionPanelProps) {
  const { question, options, allowFreeText } = decision;

  // The options are numbered, so the number keys should pick them.
  useKeyboardShortcuts({
    shortcuts: [
      ...options.map((_, index) => ({
        key: String(index + 1),
        requireModifier: false,
        handler: () => onPick(index),
      })),
      { key: 'Escape', requireModifier: false, handler: onDismiss },
    ],
    enabled: !disabled,
    // The default ignore check covers contentEditable (an open artifact
    // doc, say) as well as inputs -- the gap a hand-rolled INPUT/TEXTAREA
    // check would miss. Modifier combos are left for the browser/OS.
    shouldIgnore: (e) =>
      defaultShouldIgnoreKeyboardEvent(e) || e.metaKey || e.ctrlKey || e.altKey,
  });

  return (
    <div
      role='group'
      aria-label='Choose how to continue'
      className='flex flex-col gap-1 rounded-xl bg-accent p-3'
    >
      <div className='flex items-center gap-2 rounded-lg p-3'>
        <p className='min-w-0 flex-1 text-base wrap-break-word'>{question}</p>
        <button
          type='button'
          onClick={onDismiss}
          aria-label='Dismiss question'
          className='shrink-0 text-muted-foreground transition-colors hover:text-foreground'
        >
          <X className='size-4' />
        </button>
      </div>

      {options.map((option, index) => (
        <DecisionRow
          key={`${decision.decisionId}-${index}`}
          badge={index + 1}
          label={option}
          disabled={disabled}
          onClick={() => onPick(index)}
        />
      ))}

      {allowFreeText && (
        <DecisionRow
          badge={<Pencil className='size-4' />}
          label='Something else'
          muted
          disabled={disabled}
          onClick={onFreeText}
        />
      )}
    </div>
  );
}

interface DecisionRowProps {
  badge: React.ReactNode;
  label: string;
  muted?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function DecisionRow({
  badge,
  label,
  muted = false,
  disabled = false,
  onClick,
}: DecisionRowProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg p-2 text-left transition-colors',
        'hover:bg-background/40 disabled:pointer-events-none disabled:opacity-60',
      )}
    >
      <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-background text-base shadow-sm'>
        {badge}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 text-base wrap-break-word',
          muted && 'text-muted-foreground',
        )}
      >
        {label}
      </span>
    </button>
  );
}
